/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Base64 from "./Base64";
import Murmur3 from "./Murmur3";
import BitArray from "./BitArray";

export default class BloomFilter {
    private murmur3 = new Murmur3();
    private size = 0;
    private numHashes = 0;
    private data: BitArray;

    /*
     * @constructor
     *
     * See http://hur.st/bloomfilter for calculating
     * optimal k hashes based on m and the number of
     * false positives.
     *
     * @param {number} size The number of bits.
     * @param {number} numHashes The number of hash functions.
     * @param {Array.<number>=} opt_data
     */
    constructor(size: number, numHashes: number, optData: Array<number> = []) {
        /**
         * @type {number}
         */
        this.size = size;

        /**
         * @type {number}
         */
        this.numHashes = numHashes;

        /**
         * @type {!bloomfilter.BitArray}
         */
        if (optData.length == 0) {
            optData = new Array<number>(size);
        }
        this.data = new BitArray(size, optData);
    }
    /**
     * Seed values for the hash functions.
     *
     * @type {Array.<number>}
     */
    public static SEEDS = [0x9206774c, 0xfb6c4fdb, 0x9f727235, 0x3d827600, 0x5821f5a2, 0x52a1eb10, 0xbe648a14, 0x9e501df2, 0xb392a96f, 0x82bab341];

    /**
     * Reconstructs a serialised UnicodeTable.
     *
     * @param {string} str
     *
     * @return {!bloomfilter.BloomFilter}
     */
    public static fromBase64(str: string) {
        const binary = Base64.decode(str);
        const data: number[] = [];

        for (let i = 0; i < binary.length; i += 4) {
            const input = (binary.charCodeAt(i) << 24) | (binary.charCodeAt(i + 1) << 16) | (binary.charCodeAt(i + 2) << 8) | (binary.charCodeAt(i + 3) << 0);
            data.push(input);
        }

        const size = data.shift();
        const numHashes = data.shift();

        let finalSize = 0;
        let finalHashes = 0;
        if (size != undefined) {
            finalSize = size;
        }
        if (numHashes != undefined) {
            finalHashes = numHashes;
        }
        return new BloomFilter(finalSize, finalHashes, data);
    }

    /**
     * @param {number} items
     * @param {number} probability
     *
     * @return {number}
     */
    public static calcSize(items: number, probability: number) {
        items = items || 1;
        return Math.ceil((Math.log(probability) * items) / Math.log(1.0 / Math.pow(2.0, Math.log(2.0))));
    }

    /**
     * @param {number} items
     * @param {number} size
     *
     * @return {number}
     */
    public static calcHashes = function (items: number, size: number) {
        items = items || 1;
        return Math.max(Math.min(Math.round((Math.log(2.0) * size) / items), BloomFilter.SEEDS.length), 1);
    };

    /**
     * Returns the number of bits in the bloom filter.
     *
     * @return {number}
     */
    public getSize() {
        return this.size;
    }

    /**
     * Returns the number of hashes used in the bloom filter.
     *
     * @return {number}
     */
    public getNumHashes() {
        return this.numHashes;
    }

    /**
     * Add a Unicode code point to the bloom filter.
     *
     * @param {number|string} value
     */
    public add(value: number | string) {
        if (typeof value !== "string" && typeof value !== "number") {
            throw new Error("Value should be a string or number.");
        }

        for (let k = 0; k < this.numHashes; k++) {
            let index = 0;

            if (typeof value === "number") {
                index = this.murmur3.hashNumber(/** @type {number} */ value, BloomFilter.SEEDS[k]) % this.size;
            } else {
                index = this.murmur3.hashString(/** @type {string} */ value, BloomFilter.SEEDS[k]) % this.size;
            }
            this.data.set(index);
        }
    }

    /**
     * Test if a Unicode code point is in the bloom filter.
     *
     * Returns false if the code point is definitely not in the
     * bloom filter. Returns true if the code point is maybe in the
     * bloom filter.
     *
     * @param {number|string} value
     * @return {boolean}
     */
    public has(value: number | string) {
        if (typeof value !== "string" && typeof value !== "number") {
            throw new Error("Value should be a string or number.");
        }

        for (let k = 0; k < this.numHashes; k++) {
            let index = 0;

            if (typeof value === "number") {
                index = this.murmur3.hashNumber(/** @type {number} */ value, BloomFilter.SEEDS[k]) % this.size;
            } else {
                index = this.murmur3.hashString(/** @type {string} */ value, BloomFilter.SEEDS[k]) % this.size;
            }

            if (!this.data.has(index)) {
                return false;
            }
        }
        return true;
    }

    /**
     * Serialises the bloom filter to a base64 encoded string.
     *
     * @return {string}
     */
    public toBase64() {
        const values = [this.size, this.numHashes].concat(this.data.getValues());
        let str = "";

        for (let i = 0; i < values.length; i++) {
            const value = values[i];

            str +=
                String.fromCharCode((value & 0xff000000) >>> 24) +
                String.fromCharCode((value & 0x00ff0000) >>> 16) +
                String.fromCharCode((value & 0x0000ff00) >>> 8) +
                String.fromCharCode((value & 0x000000ff) >>> 0);
        }

        return Base64.encode(str);
    }
}
